<script>
import CandidateZoom from '@components/candidate/candidate-zoom.vue'
import BookingDateStrip from '@components/booking/booking-date-strip.vue'
import { mapActions, mapGetters } from 'vuex'
import { orderBy } from 'lodash'
import CancelBookingDialog from '@components/booking/cancel-booking-dialog.vue'
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main.vue'
import ReplaceMeBanner from '@/components/replaceMe/replace-me-banner.vue'
import dayjs from '@/services/date'
import baseMixin from '@/mixins/baseMixin'
import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import TheDashboardNavigation from '@/components/layout/the-dashboard-navigation.vue'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import OverviewStatsDto from '@/models/overview/overviewStatsDto'
import DashboardFillRateStats from '@/components/booking/dashboard-fill-rate-stats.vue'
import ClientFillRateFilter from '@/components/booking/client-fill-rate-filter.vue'
import FillRateFilterType from '@/shared/constants/booking/FillRateFilterType'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import BookingSummaryContainer from '@/components/booking/summary/booking-summary-container.vue'

export default {
  name: 'HomeView',
  components: {
    Layout,
    CandidateZoom,
    BookingDateStrip,
    ReplaceMeBanner,
    TheDashboardNavigation,
    CancelBookingDialog,
    DashboardFillRateStats,
    ClientFillRateFilter,
    BookingSummaryContainer,
  },
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: appConfig.description }],
    }
  },
  mixins: [baseMixin],
  data() {
    return {
      isLoading: false,
      arrayEvents: [],
      picker: '2021-08-20',
      selectedDay: dayjs(),
      selectedMonthYear: null,
      showCandidateModal: false,
      selectedCandidate: null,
      bookingSummary: {},
      bookingList: [],
      refreshSummaryData: false,
      defaultTempBookingsForLoader: 5,
      bookingToCancel: null,
      showCancelBookingDialog: false,
      fillRateFilterType: FillRateFilterType.selectedMonth,
      stats: null,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/currentUser',
      hasCreateBookingPermissionForAtleastOneClientLocation:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocation',
      getClient: 'auth/getClient',
      getClients: 'auth/getClients',
      getSelectedClients: 'client/getSelectedClients',
      getPermission: 'auth/getPermission',
      getNodeHierarchyByIdAndLevel: 'auth/getNodeHierarchyByIdAndLevel',
      getAllClientsLocationsWithPermission:
        'auth/getAllClientsLocationsWithPermission',
      bookingOverview: 'bookings/bookingOverview',
      isLoadingBookingSummary: 'bookings/isLoadingBookingSummary',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingPermission: 'auth/hasLocationBookingPermission',
      hasLocationPendingApprovalPermission:
        'auth/hasLocationPendingApprovalPermission',
      hasClientBookingPermission: 'auth/hasClientBookingPermission',
      hasClientPendingApprovalPermission:
        'auth/hasClientPendingApprovalPermission',
      hasPermissionRegardlessOfLevel: 'auth/hasPermissionRegardlessOfLevel',
      isClientGroupOverviewEnabled: 'auth/isClientGroupOverviewEnabled',
      homeRefreshCount: 'bookings/homeRefreshCount',
    }),
    compBookings() {
      // Check if the array is empty or if the array is full of numbers.
      // The array will only contain numbers while loading to display skeleton
      // loader cards.
      if (isNullOrEmptyArray(this.bookingList) || !isNaN(this.bookingList[0]))
        return []

      const clientLocationsWithBookings = []

      for (const client of this.bookingList) {
        const tempClientLocationsWithBookings = {
          clientId: client.clientId,
          clientName: client.clientName,
          locations: [],
        }

        for (const location of client.locations) {
          if (isNullOrEmptyArray(location.bookings)) continue

          for (const booking of location.bookings) {
            if (!booking?.blockDetails) continue

            booking.blockText = this.$t('booking.blockRangeText', {
              bookingPosition: booking.blockDetails.bookingPosition,
              totalCount: booking.blockDetails.bookingsCount,
            })

            booking.blockTooltipText = this.$t('booking.blockTooltipText', {
              startDate: dayjs(booking.blockDetails.startDateLocal)
                .locale(this.$i18n.locale.toLowerCase())
                .format('L'),
              endDate: dayjs(booking.blockDetails.endDateLocal)
                .locale(this.$i18n.locale.toLowerCase())
                .format('L'),
            })
          }

          tempClientLocationsWithBookings.locations.push(location)
        }

        // If no locations were mapped (i.e. no locations had any bookings)
        // don't add client or locations to list
        if (isNullOrEmptyArray(tempClientLocationsWithBookings.locations))
          continue

        clientLocationsWithBookings.push(tempClientLocationsWithBookings)
      }

      return clientLocationsWithBookings
    },
    canViewBookings() {
      return (
        this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
          this.getSelectedClients
        ) ||
        this.hasPermissionRegardlessOfLevel(
          PermissionScope.PENDING_BOOKING,
          PermissionModifier.VIEW
        ) ||
        this.hasPermissionRegardlessOfLevel(
          PermissionScope.PENDING_BOOKING,
          PermissionModifier.CREATE
        )
      )
    },
    isReplaceMeEnabled() {
      return this.replaceMeEnabled(this.getSelectedClients[0]) // TODO: Rework to use multiple clients
    },
    compTitle() {
      if (this.getSelectedClients.length > 1) {
        return this.$t('home.viewMultipleClientsTitle', [
          this.getSelectedClients.length,
        ])
      }

      const client = this.getClient(this.getSelectedClients[0])
      return client?.clientName || 'Unknown'
    },
    displayRandomSubtitleGreeting() {
      // Randomised Greetings
      const quotes = [
        `Hi ${this.user?.firstName?.trim() || ''}, hope you are well`,
        'Glad to have you back!',
        'Welcome back! We missed you!',
        `Hope you're having a great ${dayjs()
          .local()
          .format('dddd')}`,
      ]

      return quotes[Math.floor(Math.random() * quotes.length)]
    },
  },
  watch: {
    fillRateFilterType() {
      this.initFillRateStats()
    },
    selectedDay() {
      this.initFillRateStats()
    },
    async selectedMonthYear() {
      await this.$nextTick()
      if (this.fillRateFilterType !== FillRateFilterType.selectedMonth) return

      this.initFillRateStats()
    },
    /**
     * Listen for changes to the selected clients and refresh the data based on
     * new selection
     */
    async getSelectedClients() {
      if (!this.canViewBookings) return

      await this.refreshDashboardBookingData()

      this.initFillRateStats()
    },
    async homeRefreshCount(newVal, oldVal) {
      if (newVal > oldVal) {
        await this.refreshDashboardBookingData()
      }
    },
  },
  async created() {
    // Don't load booking summary if user doesn't have access to bookings
    if (!this.canViewBookings) return

    await this.initBookingSummaryData()

    this.initFillRateStats()
  },
  mounted() {
    if (!this.canViewBookings) return
    this.arrayEvents = [...Array(6)].map(() => {
      const day = Math.floor(Math.random() * 30)
      const d = new Date()
      d.setDate(day)
      return d.toISOString().substr(0, 10)
    })
  },
  methods: {
    ...mapActions({
      loadBookingsByClientAndDate: 'bookings/loadBookingsByClientAndDate',
      getOverviewSummaryBookingViewModels:
        'bookings/getOverviewSummaryBookingViewModels',
    }),
    getComparisonDate() {
      switch (this.fillRateFilterType) {
        case FillRateFilterType.selectedDate:
          return this.selectedDay
        case FillRateFilterType.selectedMonth: {
          // If selectedMonthYear isn't set, likely this is on page load, which
          // starts off with today's date and month (which is selectedDay's default)
          if (!this.selectedMonthYear) return this.selectedDay
          return this.selectedMonthYear
        }
      }
    },
    fillRateDateComprisonFunc(bookingVM) {
      const formatKey =
        this.fillRateFilterType === FillRateFilterType.selectedMonth
          ? DateFormatToken.YYYYMM
          : DateFormatToken.YYYYMMDD

      const comparisonDate = this.getComparisonDate()

      const selectedDate = dayjs(comparisonDate).format(formatKey)

      const bookingDate = dayjs(bookingVM.dateLocal).format(formatKey)

      return bookingDate === selectedDate
    },
    calcFillRateStats() {
      const stats = new OverviewStatsDto({})

      if (isNullOrEmptyArray(this.bookingOverview)) return stats

      const bookings = this.bookingOverview.filter(
        this.fillRateDateComprisonFunc
      )

      if (isNullOrEmptyArray(bookings)) return stats

      const result = bookings.reduce((accumulator, currentValue) => {
        accumulator.totalBookings += currentValue.totalBookingsCount
        accumulator.unfilled += currentValue.openBookingsCount
        accumulator.filled = accumulator.totalBookings - accumulator.unfilled
        return accumulator
      }, stats)

      return new OverviewStatsDto({
        openBookings: result.unfilled,
        totalBookings: result.totalBookings,
      })
    },
    initFillRateStats() {
      this.stats = this.calcFillRateStats()
    },
    generateFillRateFilterOptions() {
      return [
        {
          id: 'selected-month',
          text: this.$t('home.bookings.monthFilterLabelText', [
            dayjs(this.getComparisonDate()).format(DateFormatToken.MMMMYYYY),
          ]),
          tooltip: this.$t('home.bookings.monthFilter'),
        },
        {
          id: 'selected-day',
          text: dayjs(this.selectedDay)
            .locale(this.$i18n.locale)
            .format(DateFormatToken.ll),
          tooltip: this.$t('home.bookings.dateFilter'),
        },
      ]
    },
    orderBookings(bookingData) {
      if (isNullOrEmptyArray(bookingData)) return []

      const clients = this.getClients(this.getSelectedClients)

      if (isNullOrEmptyArray(clients)) return []

      const clientsAndLocationsWithPerms = []

      const mapBookingsToLocation = (location) => ({
        locationId: location.locationId,
        locationName: location.locationName,
        bookings: orderBy(
          bookingData?.filter(
            (booking) => booking?.location?.id === location.locationId
          ),
          ['candidate.firstName', 'candidate.lastName'],
          ['asc', 'asc']
        ),
      })

      for (const client of clients) {
        const tempClientWithLocations = {
          clientId: client.clientId,
          clientName: client.clientName,
          locations: [],
        }

        for (const location of client.locations) {
          tempClientWithLocations.locations.push(
            mapBookingsToLocation(location)
          )
        }

        if (!isNullOrEmptyArray(tempClientWithLocations.locations))
          clientsAndLocationsWithPerms.push(tempClientWithLocations)
      }

      return orderBy(clientsAndLocationsWithPerms, ['clientName'], ['asc'])
    },
    async initBookingSummaryData(initPickerDate = dayjs()) {
      this.picker = dayjs(initPickerDate).format('YYYY-MM-DD')

      this.isLoading = true

      this.fillWithTemp(this.defaultTempBookingsForLoader)

      const dayjsObject = dayjs(this.picker)

      const result = await this.loadBookingsByClientAndDate(
        dayjsObject.format('YYYY-MM-DD')
      )

      this.bookingList = this.orderBookings(result.data)
      this.isLoading = false
    },
    /**
     * Fills booking list with elements so its length matches nItems
     * @param {Number} nItems
     */
    fillWithTemp(nItems) {
      this.bookingList = Array.from(Array(nItems).keys())
    },
    async changeDay(dateObject, nItems) {
      this.selectedDay = null
      await this.$nextTick()
      this.selectedDay = dayjs(dateObject)

      // If user selected the same date as currently selected, do nothing, otherwise load bookings
      if (this.picker === dateObject) return

      this.picker = dateObject

      this.isLoading = true

      this.fillWithTemp(nItems)

      const dayjsObject = dayjs(dateObject)

      // If summary tells us that there aren't any bookings on that date then skip loading them
      if (nItems === 0) {
        this.isLoading = false
        this.bookingList = []
        return
      }

      const result = await this.loadBookingsByClientAndDate(
        dayjsObject.format(DateFormatToken.YYYYMMDD)
      )

      this.bookingList = this.orderBookings(result.data)
      this.isLoading = false
    },
    async viewTodaysBookings() {
      await this.changeDay(dayjs(), this.defaultTempBookingsForLoader) // arbitrary number since we won't know how many bookings there are in the parent
    },
    async refreshDashboardBookingData() {
      this.refreshSummaryData = true // Refresh datestrip booking data
      await this.initBookingSummaryData(this.picker) // Set date to be same as the currently selected date
    },
    showCandidateDialog(candidateId) {
      this.selectedCandidate = candidateId
      this.showCandidateModal = true
    },
    closeCandidateDialog() {
      this.selectedCandidate = null
      this.showCandidateModal = false
    },
    openCancelBookingDialog(booking) {
      this.bookingToCancel = booking
      this.showCancelBookingDialog = true
    },
    closeCancelBookingDialog() {
      this.bookingToCancel = null
      this.showCancelBookingDialog = false
    },
    async closeCancelBookingDialogAndRefresh() {
      this.closeCancelBookingDialog()
      await this.refreshDashboardBookingData()
    },
    filterChanged(value) {
      this.fillRateFilterType = value
    },
    monthChanged(value) {
      this.selectedMonthYear = value
    },
    yearChanged(value) {
      this.selectedMonthYear = value
    },
  },
}
</script>

<template>
  <Layout>
    <BasePageHeader hide-icon>
      <template v-slot:title>
        <BasePageSubtitle class="mb-1">
          {{ displayRandomSubtitleGreeting }}
        </BasePageSubtitle>
        <BasePageTitle>{{ compTitle }}</BasePageTitle>
      </template>
    </BasePageHeader>
    <div
      v-if="isClientGroupOverviewEnabled"
      :class="[
        isMediumOrLower ? 'align-start mb-4' : 'align-end',
        'd-flex flex-column',
      ]"
    >
      <v-spacer />
      <DashboardFillRateStats v-if="stats" :stats="stats" />
      <ClientFillRateFilter
        :filter="fillRateFilterType"
        :options="generateFillRateFilterOptions()"
        @filterChanged="filterChanged"
      />
    </div>
    <v-row>
      <v-col
        cols="12"
        md="12"
        lg="12"
        :class="isMobileViewPort ? 'px-0 mx-0' : ''"
      >
        <ReplaceMeBanner @refreshData="refreshDashboardBookingData" />
        <v-sheet
          v-if="canViewBookings"
          class="transparent"
          elevation="0"
          :class="isMobileViewPort ? 'px-0 mx-0' : ''"
        >
          <div :class="isMobileViewPort ? 'px-0 mx-0' : ''">
            <BookingDateStrip
              :refresh-data="refreshSummaryData"
              :selected-day="selectedDay"
              @refreshComplete="refreshSummaryData = false"
              @dateSelected="changeDay"
              @monthChanged="monthChanged"
              @yearChanged="yearChanged"
            />

            <BookingSummaryContainer
              :bookings="compBookings"
              :loading="isLoading"
              :selected-day="selectedDay"
              @viewTodaysBookings="viewTodaysBookings"
              @viewCandidate="showCandidateDialog"
              @cancelBooking="openCancelBookingDialog"
            />
          </div>
        </v-sheet>
        <TheDashboardNavigation v-else />
      </v-col>
    </v-row>
    <v-dialog
      v-if="showCandidateModal"
      v-model="showCandidateModal"
      width="720px"
      content-class="rounded-xl"
    >
      <CandidateZoom
        :id="selectedCandidate"
        @closeModal="closeCandidateDialog"
      />
    </v-dialog>
    <CancelBookingDialog
      v-if="showCancelBookingDialog"
      :show="showCancelBookingDialog"
      :booking="bookingToCancel"
      @closeModal="closeCancelBookingDialog"
      @closeModalAndRefresh="closeCancelBookingDialogAndRefresh"
    />
  </Layout>
</template>
